import { useId, useState } from 'react';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { useNamespaceCopy } from '@/i18n/ns';
import connectionsCopy from '@/i18n/ns/connections';
import { codingAgentsSource } from './coding-agents-source';
import { useCodingAgentsCopy } from './coding-agents-copy';
import type { CodingAgentGrant, RevokeResult } from './coding-agents-types';
import { relativeTime } from './format';
import { Callout } from '@/components/ui/content';
import { StatusPill } from '@/components/ui/kit';

/**
 * A list of coding agents. Access is summarised from the grant's actual scope
 * set, so an older knowledge-only grant never reads as having app access. An
 * agent asking for more shows that plainly and opens its request; revoking
 * asks once, then asks the server and re-reads the list.
 */
export function CodingAgentList({
  grants,
  label,
  showOwner,
  onReview,
  onChanged,
}: {
  grants: CodingAgentGrant[];
  label: string;
  showOwner: boolean;
  onReview: (requestId: string) => void;
  onChanged: () => void;
}) {
  const {
    REVOKE_DENIED,
    REVOKE_FAILED,
    REVOKE_GONE,
    scopeSummary,
    lastSeenLine,
    NEEDS_CONSENT,
    REVIEW_REQUEST,
    grantStateLabel,
    revokeConfirm,
    REVOKE_CONFIRM_YES,
    REVOKE_CONFIRM_NO,
    REVOKE,
  } = useCodingAgentsCopy();
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(connectionsCopy);
  const heading = useId();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const revoke = async (grant: CodingAgentGrant) => {
    if (busy) return;
    setBusy(grant.grantId);
    setMessage(null);
    let result: RevokeResult = 'failed';
    try {
      result = await codingAgentsSource().revoke(grant.grantId);
    } catch {
      result = 'failed';
    }
    setBusy(null);
    setConfirming(null);
    if (result === 'denied') setMessage(REVOKE_DENIED);
    else if (result === 'failed') setMessage(REVOKE_FAILED);
    else if (result === 'gone') setMessage(REVOKE_GONE);
    if (result !== 'denied' && result !== 'failed') onChanged();
  };

  return (
    <div className="ws-coding-list">
      <h3 id={heading} className="ws-agent-label">
        {label}
      </h3>
      <div role="status">{message && <Callout tone="danger">{message}</Callout>}</div>
      <ul className="ws-agent-list" aria-labelledby={heading}>
        {grants.map((grant) => {
          const active = grant.state === 'active';
          return (
            <li key={grant.grantId} className="ws-coding-row" data-state={grant.state}>
              <div className="ws-coding-main">
                <strong>{grant.clientName}</strong>
                {grant.originLabel && <small>{grant.originLabel}</small>}
                {showOwner && grant.ownerName && <small>{grant.ownerName}</small>}
                <span>{scopeSummary(grant.scopes)}</span>
                <small>
                  {lastSeenLine(grant, relativeTime(grant.lastSeenAt, undefined, copy, locale))}
                </small>
                {grant.pendingConsentRequestId && active && (
                  <button
                    type="button"
                    className="ws-text-button"
                    onClick={() => onReview(grant.pendingConsentRequestId as string)}
                  >
                    {NEEDS_CONSENT}: {REVIEW_REQUEST}
                  </button>
                )}
              </div>
              <div className="ws-coding-side">
                <StatusPill tone={grant.state === 'active' ? 'success' : 'neutral'}>
                  {grantStateLabel[grant.state]}
                </StatusPill>
                {active &&
                  (confirming === grant.grantId ? (
                    <span
                      className="ws-coding-confirm"
                      role="group"
                      aria-label={revokeConfirm(grant.clientName)}
                    >
                      <span>{revokeConfirm(grant.clientName)}</span>
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => void revoke(grant)}
                      >
                        {REVOKE_CONFIRM_YES}
                      </button>
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => setConfirming(null)}
                      >
                        {REVOKE_CONFIRM_NO}
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="ws-text-button"
                      onClick={() => setConfirming(grant.grantId)}
                    >
                      {REVOKE}
                    </button>
                  ))}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
